'use client';

import React from 'react';
import Link from 'next/link';
import IdeaCard from '@/components/IdeaCard';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { getPublishedIdeas, getFeaturedIdea, CATEGORIES, type Idea,  } from '@/lib/content';

const CONCEPT_CLUSTERS = [
  {
    title: 'Identity and trust',
    ideas: ['Identity is the new perimeter', 'Why MFA is not all the same', 'What zero trust actually means'],
    href: '/categories',
    color: 'var(--color-identity)',
  },
  {
    title: 'Why controls fail',
    ideas: ['One mistake should not be enough', 'Severity is not risk', 'Blast radius'],
    href: '/categories',
    color: 'var(--color-danger)',
  },
  {
    title: 'How attackers move',
    ideas: ['What lateral movement means', 'What is an attack surface', 'Blast radius'],
    href: '/categories',
    color: 'var(--accent)',
  },
  {
    title: 'Cloud assumptions',
    ideas: ['Shared responsibility in cloud', 'Identity is the new perimeter', 'What zero trust actually means'],
    href: '/categories',
    color: 'var(--color-warning)',
  },
  {
    title: 'Human behaviour',
    ideas: ['One mistake should not be enough', 'Why MFA is not all the same', 'Secure by design'],
    href: '/categories',
    color: 'var(--color-success)',
  },
  {
    title: 'Security trade-offs',
    ideas: ['Severity is not risk', 'Least privilege', 'Secure by design'],
    href: '/categories',
    color: 'var(--color-graphite)',
  },
];

const BIG_IDEAS = [
  { label: 'Security fundamentals', href: '/categories', slug: 'security-fundamentals' },
  { label: 'Identity', href: '/categories', slug: 'identity-and-access' },
  { label: 'Cloud security', href: '/categories', slug: 'cloud-security' },
  { label: 'AI security', href: '/categories', slug: 'ai-security' },
  { label: 'Application security', href: '/categories', slug: 'application-security' },
  { label: 'Resilience', href: '/categories', slug: 'architecture-and-resilience' },
];

export default function HomePage() {
  const featured = getFeaturedIdea();
  const allIdeas = getPublishedIdeas();
  const latestIdeas = allIdeas.slice(0, 6);
  const nonFeatured = allIdeas.filter(i => !i.featured).slice(0, 5);

  return (
    <>
      <main>
        {/* ── EDITORIAL OPENING ─────────────────────────────── */}
        <section className="pt-36 pb-16 sm:pt-44 sm:pb-20 px-5 sm:px-8 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left: editorial copy */}
            <div className="lg:col-span-5 flex flex-col gap-7">
              <h1
                className="text-hero-xl font-display font-bold text-primary"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Simple sketches for a safer digital world.
              </h1>

              <div className="flex flex-col gap-3 prose-editorial">
                <p className="text-lg text-foreground/80 leading-relaxed">
                  Cyber security can be complicated.
                </p>
                <p className="text-lg text-foreground/80 leading-relaxed">
                  The ideas behind it do not always have to be.
                </p>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Drawn2Security explains the concepts, mistakes, trade-offs and strange stories that shape digital security, one sketch at a time.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <Link
                  href="/explore"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-primary-foreground rounded-xl font-semibold text-sm hover:bg-primary/90 transition-all duration-200 group"
                >
                  Explore the sketches
                  <Icon name="ArrowRightIcon" size={15} variant="outline" className="group-hover:translate-x-0.5 transition-transform duration-200" />
                </Link>
                <Link
                  href="/start-here"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-card text-foreground border border-border rounded-xl font-semibold text-sm hover:border-accent hover:text-accent transition-all duration-200"
                >
                  Start here
                </Link>
              </div>

            </div>

            {/* Right: featured sketch */}
            {featured && (
              <div className="lg:col-span-7">
                <Link href={`/sketches/${featured.slug}`} className="block group">
                  <article className="sketch-card overflow-hidden">
                    <div className="relative overflow-hidden" style={{ aspectRatio: '16/10' }}>
                      <AppImage
                        src={featured.illustration_url}
                        alt={featured.illustration_alt}
                        width={900}
                        height={563}
                        priority
                        className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                    </div>
                    {/*
                      The sketch draws its own headline. A second title laid over
                      it in white, on a cream drawing, was unreadable and said the
                      same thing twice. Title sits under the image instead.
                    */}
                    <div className="p-6 sm:p-8">
                      <h2
                        className="font-display font-bold text-2xl sm:text-3xl text-primary leading-tight"
                        style={{ fontFamily: 'var(--font-display)' }}
                      >
                        {featured.title}
                      </h2>
                      <p className="text-muted-foreground leading-relaxed mt-2">{featured.reader_hook}</p>
                      <div className="flex items-center gap-1.5 text-accent text-sm font-semibold mt-4">
                        Read the sketch
                        <Icon name="ArrowRightIcon" size={14} variant="outline" className="group-hover:translate-x-0.5 transition-transform duration-200" />
                      </div>
                    </div>
                  </article>
                </Link>
              </div>
            )}
          </div>
        </section>

        <hr className="warm-divider max-w-7xl mx-auto" />

        {/* ── LATEST IDEAS ───────────────────────────────────── */}
        <section className="py-16 sm:py-20 px-5 sm:px-8 max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">New additions</p>
              <h2
                className="text-display-md font-display font-bold text-primary"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Latest ideas
              </h2>
            </div>
            <Link
              href="/explore"
              className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-accent ink-underline"
            >
              View all
              <Icon name="ArrowRightIcon" size={14} variant="outline" />
            </Link>
          </div>

          {/* Bento grid audit:
            Array has 6 cards: [idea0(wide), idea1, idea2, idea3, idea4, idea5]
            Desktop (grid-cols-3):
            Row 1: [col-1 to col-2: idea0 cs-2] [col-3: idea1 cs-1]
            Row 2: [col-1: idea2 cs-1] [col-2: idea3 cs-1] [col-3: idea4 cs-1]
            Row 3: [col-1 to col-3: idea5 cs-3]
            Placed 6/6 cards ✓
          */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Card 0, wide */}
            {latestIdeas[0] && (
              <div className="sm:col-span-2 lg:col-span-2">
                <IdeaCard idea={latestIdeas[0]} variant="featured" />
              </div>
            )}
            {/* Card 1 */}
            {latestIdeas[1] && (
              <div className="lg:col-span-1">
                <IdeaCard idea={latestIdeas[1]} />
              </div>
            )}
            {/* Cards 2, 3, 4 */}
            {latestIdeas.slice(2, 5).map((idea) => (
              <div key={idea.id}>
                <IdeaCard idea={idea} />
              </div>
            ))}
            {/* Card 5, full width */}
            {latestIdeas[5] && (
              <div className="sm:col-span-2 lg:col-span-3">
                <IdeaCard idea={latestIdeas[5]} variant="featured" />
              </div>
            )}
          </div>

          <div className="flex justify-center mt-8 sm:hidden">
            <Link
              href="/explore"
              className="inline-flex items-center gap-2 px-6 py-3 border border-border rounded-xl text-sm font-medium text-foreground hover:border-accent hover:text-accent transition-all"
            >
              View all sketches
              <Icon name="ArrowRightIcon" size={14} variant="outline" />
            </Link>
          </div>
        </section>

        <hr className="warm-divider max-w-7xl mx-auto" />

        {/* ── START WITH THE BIG IDEAS ───────────────────────── */}
        <section className="py-16 sm:py-20 px-5 sm:px-8 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Where to begin</p>
              <h2
                className="text-display-md font-display font-bold text-primary mb-4"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Start with the big ideas
              </h2>
              <p className="text-muted-foreground leading-relaxed text-base mb-6">
                These are the categories that form the foundation. Understanding these makes everything else click into place.
              </p>
              <Link
                href="/start-here"
                className="inline-flex items-center gap-2 text-sm font-semibold text-accent ink-underline"
              >
                See the guided sequence
                <Icon name="ArrowRightIcon" size={14} variant="outline" />
              </Link>
            </div>

            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {BIG_IDEAS.map((item, i) => {
                  const cat = CATEGORIES.find(c => c.slug === item.slug);
                  return (
                    <Link
                      key={item.slug}
                      href={item.href}
                      className="group flex items-center justify-between px-5 py-4 rounded-xl border border-border bg-card hover:border-accent hover:bg-muted/50 transition-all duration-200 card-lift"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className="font-display text-xs font-bold text-muted-foreground"
                          style={{ fontFamily: 'var(--font-display)', minWidth: '1.5rem' }}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className="font-medium text-foreground text-sm group-hover:text-accent transition-colors duration-200">
                          {item.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {cat && (
                          <span className="text-xs text-muted-foreground">{cat.idea_count} ideas</span>
                        )}
                        <Icon name="ArrowRightIcon" size={14} variant="outline" className="text-muted-foreground group-hover:text-accent group-hover:translate-x-0.5 transition-all duration-200" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <hr className="warm-divider max-w-7xl mx-auto" />

        {/* ── EXPLORE CYBER SECURITY ─────────────────────────── */}
        <section className="py-16 sm:py-20 px-5 sm:px-8 max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">Browse by topic</p>
              <h2
                className="text-display-md font-display font-bold text-primary"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Explore cyber security
              </h2>
            </div>
            <Link
              href="/categories"
              className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-accent ink-underline"
            >
              All categories
              <Icon name="ArrowRightIcon" size={14} variant="outline" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CATEGORIES.slice(0, 9).map((cat) => (
              <Link
                key={cat.id}
                href="/categories"
                className="category-editorial-card group"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                    {cat.idea_count} ideas
                  </span>
                  <Icon name="ArrowUpRightIcon" size={14} variant="outline" className="text-muted-foreground group-hover:text-accent transition-colors duration-200" />
                </div>
                <h3
                  className="font-display font-semibold text-foreground text-lg leading-snug mb-2 group-hover:text-accent transition-colors duration-200"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  {cat.name}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                  {cat.editorial_intro}
                </p>
              </Link>
            ))}
          </div>
        </section>

        <hr className="warm-divider max-w-7xl mx-auto" />

        {/* ── RANDOM IDEA ────────────────────────────────────── */}
        <section className="py-12 px-5 sm:px-8 max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-5 p-7 rounded-2xl border border-border bg-card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                <Icon name="SparklesIcon" size={22} variant="outline" className="text-accent" />
              </div>
              <div>
                <h3
                  className="font-display font-semibold text-foreground text-lg"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  Teach me something random
                </h3>
                <p className="text-sm text-muted-foreground">Pick a sketch and see where it leads.</p>
              </div>
            </div>
            <Link
              href={`/sketches/${allIdeas[Math.floor(allIdeas.length / 2)]?.slug || allIdeas[0]?.slug}`}
              className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-all duration-200 group"
            >
              Random idea
              <Icon name="ArrowRightIcon" size={14} variant="outline" className="group-hover:translate-x-0.5 transition-transform duration-200" />
            </Link>
          </div>
        </section>

        <hr className="warm-divider max-w-7xl mx-auto" />

        {/* ── POPULAR CONNECTIONS ────────────────────────────── */}
        <section className="py-16 sm:py-20 px-5 sm:px-8 max-w-7xl mx-auto">
          <div className="mb-10">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">Concept clusters</p>
            <h2
              className="text-display-md font-display font-bold text-primary"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              Popular connections
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CONCEPT_CLUSTERS.map((cluster) => (
              <Link key={cluster.title} href={cluster.href} className="cluster-card group block">
                <div
                  className="w-2 h-2 rounded-full mb-4"
                  style={{ background: cluster.color }}
                />
                <h3
                  className="font-display font-semibold text-foreground text-base mb-3 group-hover:text-accent transition-colors duration-200"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  {cluster.title}
                </h3>
                <ul className="flex flex-col gap-1.5">
                  {cluster.ideas.map((idea) => (
                    <li key={idea} className="text-sm text-muted-foreground flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-border flex-shrink-0" />
                      {idea}
                    </li>
                  ))}
                </ul>
              </Link>
            ))}
          </div>
        </section>

        <hr className="warm-divider max-w-7xl mx-auto" />

        {/* ── NEWSLETTER ─────────────────────────────────────── */}
        <section className="py-16 sm:py-20 px-5 sm:px-8 max-w-7xl mx-auto">
          <div className="max-w-xl mx-auto text-center">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Stay curious</p>
            <h2
              className="font-display font-bold text-primary mb-4"
              style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', lineHeight: '1.1' }}
            >
              A new sketch, when it is ready.
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              No noise. Just a useful idea, explained clearly. Unsubscribe whenever you like.
            </p>
            <form
              onSubmit={(e) => e.preventDefault()}
              className="flex flex-col sm:flex-row gap-3"
            >
              <input
                type="email"
                placeholder="your@email.com"
                className="search-input pl-5 flex-1"
                style={{ paddingLeft: '1.25rem' }}
              />
              <button
                type="submit"
                className="flex-shrink-0 px-6 py-3.5 bg-primary text-primary-foreground rounded-xl font-semibold text-sm hover:bg-primary/90 transition-all duration-200"
              >
                Subscribe
              </button>
            </form>
            <p className="text-xs text-muted-foreground mt-3">No spam. No vendor content. Just ideas.</p>
          </div>
        </section>
      </main>
    </>
  );
}